// The member's notification preferences (NTF-017/018; bead hf-e7-notifications-742.3).
//
// ── WHAT THIS SURFACE IS CAREFUL ABOUT ─────────────────────────────────────────
//
// Five of the seven categories cannot be switched off, and how that is SHOWN is the
// whole design problem. A disabled toggle is a picture of something being withheld: it
// invites somebody to try, fail, and feel managed by an app they agreed to use to help
// their family. So an always-on category renders no switch at all — it renders the
// sentence explaining what the message is, and the channel choice that genuinely is
// theirs. "Someone is waiting on your answer to this one, so it always reaches you.
// You can still choose how."
//
// EXCEPT THAT SENTENCE IS NOT TRUE OF ALL OF THEM (bead hf-6j6). A cancellation always
// arrives and asks nothing; claiming somebody waits on an answer to it would be false
// on the screen where a member decides how to be reached. `alwaysOnReasonFor` picks the
// reviewed sentence that fits, and product-language still owns both.
//
// THE RULES COME FROM THE SERVER, not from a list here. `mutable` arrives on each
// category in the read model, so a category whose mutability changes changes here
// without an edit — and the browser cannot disagree with what the dispatcher will
// actually honour (AUT-007).
//
// ── AND THE CIRCLE POLICY IS STATED IN BOTH DIRECTIONS ─────────────────────────
//
// NTF-018 requires the quiet-hours override to be visible. A surface that only said
// something when the policy was ON would leave everybody else to infer the answer, so
// both states get a sentence. The switch that CHANGES it appears only for somebody the
// server says may change it.

import { Button, Card, StatusMessage, TextInput } from "@herefor/design-system";
import { NOTIFICATION_PREFERENCE_MESSAGES as COPY } from "@herefor/product-language";
import type { ReactNode } from "react";

export type NotificationChannel = "sms" | "email";

export interface NotificationCategoryView {
  readonly category: keyof typeof COPY.categories;
  readonly mutable: boolean;
  readonly muted: boolean;
  readonly channels: readonly string[];
}

export interface NotificationPreferencesView {
  readonly categories: readonly NotificationCategoryView[];
  readonly quietHours: { readonly startLocal: string; readonly endLocal: string } | null;
  readonly circleAllowsQuietHoursOverride: boolean;
  readonly canManageCirclePolicy: boolean;
}

export type SaveState =
  | { readonly status: "idle" }
  | { readonly status: "saving" }
  | { readonly status: "saved" }
  | { readonly status: "failed"; readonly message: string };

export interface NotificationPreferencesProps {
  readonly preferences: NotificationPreferencesView;
  readonly state: SaveState;
  readonly onToggleMuted: (category: NotificationCategoryView["category"]) => void;
  readonly onToggleChannel: (
    category: NotificationCategoryView["category"],
    channel: NotificationChannel,
  ) => void;
  readonly onQuietHoursChange: (bounds: { startLocal: string; endLocal: string } | null) => void;
  readonly onSave: () => void;
  /** Absent when the server says this member may not change the circle's policy. */
  readonly onCirclePolicyChange?: (allowed: boolean) => void;
  readonly actionsDisabled?: boolean;
}

export function NotificationPreferences({
  preferences,
  state,
  onToggleMuted,
  onToggleChannel,
  onQuietHoursChange,
  onSave,
  onCirclePolicyChange,
  actionsDisabled = false,
}: NotificationPreferencesProps) {
  const busy = actionsDisabled || state.status === "saving";

  return (
    <section
      aria-labelledby="notification-preferences-heading"
      className="notification-preferences"
    >
      <h2 id="notification-preferences-heading">{COPY.heading}</h2>
      <p className="notification-preferences__introduction">{COPY.introduction}</p>

      <ul className="notification-preferences__list">
        {preferences.categories.map((category) => (
          <li key={category.category}>
            <CategoryCard
              busy={busy}
              category={category}
              onToggleChannel={onToggleChannel}
              onToggleMuted={onToggleMuted}
            />
          </li>
        ))}
      </ul>

      <QuietHours
        busy={busy}
        preferences={preferences}
        onQuietHoursChange={onQuietHoursChange}
        {...(onCirclePolicyChange === undefined ? {} : { onCirclePolicyChange })}
      />

      <div className="notification-preferences__actions">
        <Button disabled={busy} onClick={onSave} variant="primary">
          {COPY.save}
        </Button>
        {state.status === "saved" ? (
          <StatusMessage tone="success">{COPY.saved}</StatusMessage>
        ) : null}
        {state.status === "failed" ? (
          <StatusMessage tone="warning">{state.message}</StatusMessage>
        ) : null}
      </div>
    </section>
  );
}

/**
 * Why THIS category always arrives.
 *
 * Most of them are waiting on the member's answer, and one sentence covers all of
 * those. A cancellation is not: it asks nothing, and telling somebody an answer is
 * expected of it would be false on the one screen where they decide how to be reached.
 * Copy still comes entirely from product-language — this only chooses which reviewed
 * sentence applies.
 */
function alwaysOnReasonFor(category: NotificationCategoryView["category"]): string {
  const byCategory: Partial<Record<NotificationCategoryView["category"], string>> =
    COPY.alwaysOnReasonByCategory;
  return byCategory[category] ?? COPY.alwaysOnReason;
}

function CategoryCard({
  category,
  busy,
  onToggleMuted,
  onToggleChannel,
}: {
  readonly category: NotificationCategoryView;
  readonly busy: boolean;
  readonly onToggleMuted: NotificationPreferencesProps["onToggleMuted"];
  readonly onToggleChannel: NotificationPreferencesProps["onToggleChannel"];
}) {
  const copy = COPY.categories[category.category];
  const chosen = new Set(category.channels);
  const noChannel = chosen.size === 0;

  return (
    <Card title={copy.title} titleLevel={3}>
      <p className="notification-category__body">{copy.body}</p>

      {category.mutable ? (
        <p className="notification-category__choice">
          <label>
            <input
              checked={!category.muted}
              disabled={busy}
              onChange={() => {
                onToggleMuted(category.category);
              }}
              type="checkbox"
            />{" "}
            {COPY.muteLabel}
          </label>
        </p>
      ) : (
        // NO DISABLED SWITCH. A control somebody cannot use is a picture of a refusal;
        // the sentence is the same fact told as information, and it ends by pointing
        // at the choice that IS theirs.
        <p className="notification-category__always-on">
          <strong>{COPY.alwaysOnLabel}.</strong> {alwaysOnReasonFor(category.category)}
        </p>
      )}

      {category.muted ? null : (
        <fieldset className="notification-category__channels">
          <legend>{COPY.channelsLegend}</legend>
          {(["sms", "email"] as const).map((channel) => (
            <label className="notification-category__choice" key={channel}>
              <input
                checked={chosen.has(channel)}
                disabled={busy}
                onChange={() => {
                  onToggleChannel(category.category, channel);
                }}
                type="checkbox"
              />{" "}
              {COPY.channels[channel]}
            </label>
          ))}
        </fieldset>
      )}

      {/* Said plainly rather than prevented. Somebody may be mid-edit on their way to
          a different combination, and a form that refused the intermediate state would
          be arguing with them. */}
      {noChannel && !category.muted ? (
        <StatusMessage tone="warning">{COPY.noChannelWarning}</StatusMessage>
      ) : null}
    </Card>
  );
}

function QuietHours({
  preferences,
  busy,
  onQuietHoursChange,
  onCirclePolicyChange,
}: {
  readonly preferences: NotificationPreferencesView;
  readonly busy: boolean;
  readonly onQuietHoursChange: NotificationPreferencesProps["onQuietHoursChange"];
  readonly onCirclePolicyChange?: (allowed: boolean) => void;
}): ReactNode {
  const quiet = preferences.quietHours;

  return (
    <section aria-labelledby="quiet-hours-heading" className="notification-quiet-hours">
      <h3 id="quiet-hours-heading">{COPY.quietHoursHeading}</h3>
      {/* HELD, NOT DROPPED — said on the surface, because a member deciding whether to
          set quiet hours is deciding whether they will miss things, and they will not. */}
      <p>{COPY.quietHoursBody}</p>

      <div className="notification-quiet-hours__fields">
        <TextInput
          disabled={busy}
          label={COPY.quietHoursStart}
          onChange={(event) => {
            onQuietHoursChange({
              startLocal: event.target.value,
              endLocal: quiet?.endLocal ?? "07:00",
            });
          }}
          type="time"
          value={quiet?.startLocal ?? ""}
        />
        <TextInput
          disabled={busy}
          label={COPY.quietHoursEnd}
          onChange={(event) => {
            onQuietHoursChange({
              startLocal: quiet?.startLocal ?? "22:00",
              endLocal: event.target.value,
            });
          }}
          type="time"
          value={quiet?.endLocal ?? ""}
        />
        {quiet === null ? (
          <p>{COPY.quietHoursNone}</p>
        ) : (
          <Button
            disabled={busy}
            onClick={() => {
              onQuietHoursChange(null);
            }}
            variant="secondary"
          >
            {COPY.clearQuietHours}
          </Button>
        )}
      </div>

      {/* BOTH DIRECTIONS. A surface that spoke only when the policy was on would leave
          everybody else to infer the answer at three in the morning (NTF-018). */}
      <p className="notification-quiet-hours__policy">
        {preferences.circleAllowsQuietHoursOverride
          ? COPY.overrideAllowed
          : COPY.overrideNotAllowed}
      </p>

      {onCirclePolicyChange === undefined ? null : (
        <fieldset className="notification-category__channels">
          <legend>{COPY.circlePolicyLegend}</legend>
          <label className="notification-category__choice">
            <input
              checked={preferences.circleAllowsQuietHoursOverride}
              disabled={busy}
              onChange={(event) => {
                onCirclePolicyChange(event.target.checked);
              }}
              type="checkbox"
            />{" "}
            {COPY.circlePolicyLabel}
          </label>
          <p className="notification-category__body">{COPY.circlePolicyHint}</p>
        </fieldset>
      )}
    </section>
  );
}
